import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AtmosAuth } from '@blk/idm-okta-sdk-js';
import { OktaError } from '@ddt-web/error-handler';
import { InjectSessionStorage, SessionStorageKey } from '@ddt-web/generic-utils';
import { UrlUtils } from '@ddt-web/url';
import * as get from 'lodash/get';
import * as set from 'lodash/set';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';

import { BaseHrefRetrieverService } from '../app-bootstrap/base-href-retriever.service';
import { AuthUtil } from './auth.util';
import { OktaConfig } from './okta-config.interface';
import { OktaConstants } from './okta.constants';


@Injectable({
	providedIn: 'root'
})
export class OktaAuthenticationService {
	@InjectSessionStorage(SessionStorageKey.OktaRedirect) private oktaRedirectUrl: string;

	public readonly atmosAuth: AtmosAuth;
	public readonly isAuthenticated$: Subject<boolean> = new BehaviorSubject<boolean>(false);

	private config: OktaConfig;
	private envConfig: any;
	private readonly SIGN_IN_APP_OKTA: string = 'signInAppWithOkta';

	constructor(
		@Inject('environment') private environment: any,
		private router: Router,
		private baseHrefRetrieverService: BaseHrefRetrieverService
	) {
		if (AuthUtil.isOktaEnabled(environment.cloud)) {
			this.getOktaConfigFromEnvironment();
			this.config = this.retrieveConfig();
			this.atmosAuth = new AtmosAuth(this.config);
		}
	}

	public getAccessToken(): string {
		return this.atmosAuth?.getAccessToken();
	}

	public getIdToken(): string {
		return this.atmosAuth?.getIdToken();
	}

	public getApiKey(): string {
		return window?.['BLK']?.['ApigeeApiKey'] ?? this.environment?.apigeeApiKey;
	}

	public refreshTokens(): Observable<boolean> {
		return from(this.atmosAuth?.retrieveAndStoreNewTokens(this.config)).pipe(map(() => true), catchError(() => of(false)));
	}

	public checkAuthentication(authSuccessCallback?: Function): void {
		const urlSearchParams = new URLSearchParams(location.search);
		const isAuthenticated = this.atmosAuth?.isAuthenticated();

		if (isAuthenticated) {
			// Step 3 - SUCCESSFUL login
			this.performAuthSuccess(authSuccessCallback);

		} else if (urlSearchParams.has('state')) {
			// Step 2 - when the user is redirected back from Okta

			if (urlSearchParams.has('code')) {
				if (UrlUtils.isLocalhost() && this.oktaRedirectUrl) {
					// Force refresh of page with okta vars + what was requested before okta redirect
					const mergedUrl = this.mergeQueryParams(this.oktaRedirectUrl, window.location.href);
					if (window.location.href !== mergedUrl) {
						this.oktaRedirectUrl = null;
						window.location.href = mergedUrl;
						return;
					}
				}

				// Remove query params
				const currentUrl = new URL(window.location.href);
				const urlSearchParams = new URLSearchParams(currentUrl.search);
				OktaConstants.QUERY_PARAMS.forEach(param => {
					urlSearchParams.delete(param);
				});
				currentUrl.search = urlSearchParams.toString();

				from(this.atmosAuth.retrieveAndStoreTokens()).pipe(first()).subscribe(() => {
					window.history.replaceState({}, document.title, currentUrl.toString());

					if (!this.atmosAuth.isAuthenticated()) {
						throw new OktaError('ERRCLK', 'It appears your computer system clock is incorrect. Please check and try again.');
					}

					this.checkAuthentication(authSuccessCallback);
				}, error => {
					console.error('Error with retrieving tokens', error);
					throw new OktaError('ERRTKN', 'Error with your authentication', currentUrl.toString());
				});
			} else {
				const error = decodeURIComponent(urlSearchParams.get('error_description') || '');
				console.error('Error while logging in.', error);
				throw new OktaError('ERRCDE', 'Error with your authentication');
			}
		} else if (UrlUtils.isLocalhost()) {

			this.loginUser();

		} else if (!window.location.search.includes(this.SIGN_IN_APP_OKTA)) {

			const url = new URL(window.location.href);
			url.searchParams.set(this.SIGN_IN_APP_OKTA, 'true');
			window.location.href = url.toString();

		} else {
			throw new OktaError('ERRGEN', 'Error with your authentication');
		}
	}

	public isUserAuthenticated(): Observable<boolean> {
		return this.isAuthenticated$;
	}

	public loginUser(redirectUrl: string = null): void {
		let url: string;
		if (redirectUrl) {
			// this is mainly for models with custom redirect url
			url = new URL(decodeURIComponent(redirectUrl), window.location.href).toString();
		} else {
			url = window.location.href;
		}
		sessionStorage.setItem(SessionStorageKey.OktaRedirect, url);
		this.atmosAuth.login();
	}

	private retrieveConfig(): OktaConfig {
		const isProd = window['globalAnalyticsParams']?.envType === 'prod';
		let env;

		if (UrlUtils.isAzrc()) {
			env = 'AZRC';
		} else if (UrlUtils.isPreview()) {
			env = 'PREVIEW';
		} else if (isProd || UrlUtils.isProdDmz()) {
			env = 'BLK';
		} else if (UrlUtils.isInternalHost()) {
			env = 'TST';
		} else {
			env = 'TESTC';
		}

		const redirectUri = this.getRedirectUri();

		const config: OktaConfig = {
			clientId: window?.['BLK']?.OktaConfig?.clientId ?? this.envConfig?.clientId,
			env,
			issuer: window?.['BLK']?.OktaConfig?.issuer ?? this.envConfig?.issuer,
			redirectUri,
			logoutUri: 'https://www.blackrock.com', // we dont use logout uri since CWP does the logout, so hardcoding this to blackrock.com as advised by UP team
			refresh: true,
			site: 'blk'
		};
		return config;
	}


	private performAuthSuccess(authSuccessCallback: Function): void {

		// Execute the success callback
		if (authSuccessCallback !== undefined && typeof authSuccessCallback === 'function') {
			authSuccessCallback();
		}

		this.isAuthenticated$.next(true);

		// Call CWP components success API gateway callback
		try {
			if (window['BLK']) {
				set(window, 'BLK.apiGateway._isAuthenticated', true);
				if (get(window, 'BLK.apiGateway._isAuthenticating')) {
					window['BLK'].apiGateway._executeCallbacks(window['BLK'].apiGateway._callbackQueue.success, true);
				}
			}
		} catch (e) {
			console.error('CWP callback failed', e);
		}
	}

	private getRedirectUri(): string {
		const redirectUriFromBLK = window?.['BLK']?.OktaConfig?.redirectUri ?? this.envConfig?.redirectUri;
		if (redirectUriFromBLK) {
			return redirectUriFromBLK;
		}

		const redirectUri = this.baseHrefRetrieverService.getBaseHref();
		const finalUrl = new URL(redirectUri, window.location.href).toString();
		return finalUrl.endsWith('/') ? finalUrl.slice(0, -1) : finalUrl;
	}

	/**
	 * This is a temp function for getting okta config from environmrnt file till the time we get the changes by CWP
	 * @returns redirectURI
	 */
	private getOktaConfigFromEnvironment(): void {
		if (window.location.href.includes('testc')) {
			this.envConfig = this.environment?.oktaConfig?.test;
		} else if (window.location.href.includes('preview')) {
			this.envConfig = this.environment?.oktaConfig?.preview;
		} else if (window.location.href.includes('localhost')) {
			this.envConfig = this.environment?.oktaConfig;
		} else if (window.location.href.includes('tst')) {
			this.envConfig = this.environment?.oktaConfig;
		} else {
			this.envConfig = this.environment?.oktaConfig?.blackrock;
		}
	}

	private mergeQueryParams(url1: string, url2: string): string {
		// Parse the query parameters from both URLs
		const params1 = new URLSearchParams(url1.split('?')[1]);
		const params2 = new URLSearchParams(url2.split('?')[1]);

		// Merge and deduplicate the parameters
		const mergedParams = new URLSearchParams(params1.toString());

		params2.forEach((value, key) => {
			// Check if the parameter already exists to avoid duplicates
			if (!mergedParams.has(key)) {
				mergedParams.set(key, value);
			}
		});

		// Combine the URL with the merged query parameters
		const mergedUrl = url1.split('?')[0] + '?' + mergedParams.toString();

		return mergedUrl;
	}
}
